import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { Spinner } from '../../../../common/index.jsx';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import CollapseBlock from '../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import PropTypes from 'prop-types';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { Flex, Radio, Tag } from 'antd';
import { SeriousDeleteQuestion } from '../../../../common/DeleteQuestion/SeriousDeleteQuestion.jsx';
import { useSearchParams } from 'react-router-dom';

const ExternalUsersTable = ({
  usersData,
  onDeleteClick,
  onEditClick,
  onActivateClick,
  onDeactivateClick,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [selectedUserListOption, setSelectedUserListOption] = useState(() => {
    let value =
      searchParams.get('form') !== null &&
      localStorage.getItem('listUserTypeOption') !== null
        ? localStorage.getItem('listUserTypeOption')
        : 'active';
    localStorage.removeItem('listUserTypeOption');
    return value;
  });

  const filteredData = useMemo(() => {
    switch (selectedUserListOption) {
      case 'active':
        return usersData.filter((user) => user.approved_at);
      case 'inactive':
        return usersData.filter((user) => !user.approved_at);
      default: {
        return [];
      }
    }
  }, [usersData, selectedUserListOption]);

  const userListOptionText = {
    active: t('Zoznam aktívnych externých používateľov'),
    inactive: t('Zoznam neaktívnych externých používateľov'),
  };

  const handleRadioChange = (e) => {
    setSelectedUserListOption(e.target.value);
    localStorage.setItem('listUserTypeOption', e.target.value);
  };

  // Custom settings
  const customSettings = (row) => {
    return (
      <>
        {selectedUserListOption === 'inactive' && (
          <IconButton
            icon={<CheckOutlined />}
            tooltip={t('Povoliť prístup')}
            onClick={(e) => {
              e.stopPropagation();
              onActivateClick(row);
            }}
          />
        )}
        {selectedUserListOption === 'active' && (
          <IconButton
            icon={<CloseOutlined />}
            tooltip={t('Zrušiť prístup')}
            onClick={(e) => {
              e.stopPropagation();
              onDeactivateClick(row);
            }}
          />
        )}
      </>
    );
  };

  // Sorters
  const lastNameTextFilterProps = useDataGridTextFilter('last_name');
  const firstNameTextFilterProps = useDataGridTextFilter('first_name');
  const external_partnerNameTextFilterProps = useDataGridTextFilter([
    'external_partner',
    'name',
  ]);
  const emailTextFilterProps = useDataGridTextFilter('email');
  const phone_numberTextFilterProps = useDataGridTextFilter('phone_number');
  const created_atDateFilterProps = useDataGridDateRangeFilter('created_at');

  // Columns
  const dataGridColumns = useMemo(
    () => [
      {
        title: t('Priezvisko'),
        dataIndex: 'last_name',
        ellipsis: true,
        sorter: dataGridTextSorter('last_name'),
        defaultSortOrder: 'ascend',
        ...lastNameTextFilterProps,
      },
      {
        title: t('Krstné meno'),
        dataIndex: 'first_name',
        ellipsis: true,
        sorter: dataGridTextSorter('first_name'),
        ...firstNameTextFilterProps,
      },
      {
        title: t('Externý partner FRI'),
        dataIndex: ['external_partner', 'name'],
        ellipsis: true,
        sorter: dataGridTextSorter('external_partner'),
        ...external_partnerNameTextFilterProps,
      },
      {
        title: t('E-mail'),
        dataIndex: 'email',
        ellipsis: true,
        sorter: dataGridTextSorter('email'),
        ...emailTextFilterProps,
      },
      {
        title: t('Telefónne číslo'),
        dataIndex: 'phone_number',
        ellipsis: true,
        sorter: dataGridTextSorter('phone_number'),
        ...phone_numberTextFilterProps,
      },
      {
        title: t('Vytvorený dňa'),
        dataIndex: 'created_at',
        ellipsis: true,
        sorter: dataGridTextSorter('created_at'),
        render: (date) =>
          (date && new Date(date).toLocaleString('sk-SK')) || '-',
        ...created_atDateFilterProps,
      },
      {
        title: t('Status'),
        dataIndex: 'state',
        ellipsis: true,
        render: (_, row) => (
          <Tag color={row.approved_at ? 'green' : 'red'}>
            {row.approved_at ? 'Aktívny' : 'Neaktívny'}
          </Tag>
        ),
      },
    ],
    [t],
  );

  return !filteredData ? (
    <Spinner />
  ) : (
    <>
      <div>
        <CollapseBlock
          collapsible={true}
          title={t('Filter externých používateľov')}
        >
          <Flex style={{ alignItems: 'baseline' }} gap="middle">
            <Flex vertical gap="middle">
              <Radio.Group
                block
                optionType="button"
                value={selectedUserListOption}
                onChange={handleRadioChange}
                style={{ whiteSpace: 'nowrap' }}
              >
                <Radio value="active">{userListOptionText['active']}</Radio>
                <Radio value="inactive">{userListOptionText['inactive']}</Radio>
              </Radio.Group>
            </Flex>
          </Flex>
        </CollapseBlock>
        <CollapseBlock
          collapsible={true}
          title={userListOptionText[selectedUserListOption]}
        >
          <DataGrid
            rowKey={(user) => user.id}
            columns={dataGridColumns}
            dataSource={filteredData || []}
            permissionsBasic={'external-users'}
            uniqueTableId={'external-users-table'}
            onDelete={onDeleteClick}
            onEditClick={onEditClick}
            onRowClick={onEditClick}
            customSettings={customSettings}
            question={
              <SeriousDeleteQuestion
                firstText={t(
                  'Naozaj chcete vymazať tohto externého používateľa?',
                )}
                mainNotice={t(
                  'Spolu s ním sa vymažú všetky pracovné ponuky a ostatné údaje s ním spojené.',
                )}
                lastText={t(
                  'Prosím uistite sa, že naozaj chcete vymazať externého používateľa. Operácia sa nedá vrátiť späť!',
                )}
              />
            }
          />
        </CollapseBlock>
      </div>
    </>
  );
};

ExternalUsersTable.propTypes = {
  usersData: PropTypes.array,
  onDetailClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onDeactivateClick: PropTypes.func,
  onActivateClick: PropTypes.func,
};

export default ExternalUsersTable;
